import { useEffect, useState } from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import AdminToggle from "../components/AdminToggle";
import CoachLibraryDownload from "../components/CoachLibraryDownload";
import ResetButton from "../components/ResetButton";

const SOURCE_LABELS = {
  "coach-food": "Coach's custom foods",
  "coach-meal": "Coach's meals",
  "new-meal": "New meals (created now)",
  "coach-recipe": "Coach's recipes",
  "organization-recipe": "Organization's recipes",
  "public-recipe": "MSB public recipes",
  "verified-food": "MSB verified foods",
  "general-food:usda": "MSB foods (USDA)",
  "general-food:afcd": "MSB foods (AFCD)",
  "recipe-ingredients": "Recipe ingredients (recipe not found)",
  "new-food": "New custom foods",
};

const sourceLabel = (source) => {
  if (SOURCE_LABELS[source]) return SOURCE_LABELS[source];
  if (source.startsWith("general-food:"))
    return `MSB foods (${source.slice("general-food:".length).toUpperCase()})`;
  return source;
};

const optionValue = ({ source, id }) => `${source}|${id}`;

const optionText = (option) =>
  [
    option.brand ? `${option.name} (${option.brand})` : option.name,
    sourceLabel(option.source),
    option.kcal === undefined ? null : `${option.kcal} kcal/100 ${option.per}`,
  ]
    .filter(Boolean)
    .join(" · ");

/** The picks the previewed summary already applies, as { food name: { source, id } }. */
const appliedChoices = (summary) =>
  Object.fromEntries(
    (summary?.chosen ?? []).map((food) => [food.name, food.choice])
  );

/** Names whose pick differs from the one the shown preview applies. */
const changedNames = (choices, applied) => {
  const same = (a, b) => a?.source === b?.source && a?.id === b?.id;
  return new Set(
    [...Object.keys(choices), ...Object.keys(applied)].filter(
      (name) => !same(choices[name], applied[name])
    )
  );
};

const sameChoices = (a, b) => {
  const canonical = (choices) =>
    JSON.stringify(
      Object.entries(choices)
        .map(([name, { source, id }]) => [name, source, id])
        .sort()
    );
  return canonical(a) === canonical(b);
};

const amountText = ({ kind, quantity, unit }) => {
  if (quantity === undefined) return "";
  if (kind === "meal") return `${quantity}x`;
  if (kind === "recipe")
    return `${quantity} serving${quantity === 1 ? "" : "s"}`;
  return `${quantity} ${unit}`;
};

const macrosText = (food) =>
  `${food.calories} kcal · ${food.proteins}P ${food.carbs}C ${food.fats}F per ${food.measurementsValue} ${food.unitOfMeasure}`;

const MODE_LABELS = { detailed: "Detailed", simplified: "Simplified" };

/**
 * Admin: import a coach's meal plan from the JSON Claude made from their file
 * (msb-api script/import-meal-plan.prompt.md). It becomes a meal plan template
 * of that coach. Preview is a dry run; a clean preview offers Import, which
 * writes that same JSON.
 */
const ImportMealPlan = () => {
  const { locals } = useRillContext();
  const { importResult, query = {} } = locals;

  return (
    <main className="admin">
      <AdminToggle />

      <div className="admin-import">
        <header className="admin-import-header">
          <h2>Import Meal Plan</h2>
          <p>
            Download the coach's library for Claude, then upload the JSON Claude
            made from the meal plan. The plan becomes a template in the coach's
            account. Preview checks it without writing anything.
          </p>
        </header>

        <CoachLibraryDownload
          defaultEmail={query.coachEmail}
          libraryPath="/admin/import-meal-plan/library"
          description="The coach's custom foods, meals and recipes, and the MSB food sources they use. Attach it to Claude when converting this coach's meal plans."
        />

        <form
          className="admin-import-form"
          // keep the chosen file after Preview (rill resets forms on submit)
          data-noreset
          method="POST"
          action="/actions/admin-panel/import-meal-plan"
          encType="multipart/form-data"
        >
          <div className="form-row">
            <div className="form-row-item">
              <label htmlFor="import-coach-email">Coach email</label>
              <input
                id="import-coach-email"
                type="email"
                name="coachEmail"
                placeholder="coach@example.com"
                required
                defaultValue={query.coachEmail}
              />
            </div>
            <div className="form-row-item">
              <label htmlFor="import-title">Template name</label>
              <input
                id="import-title"
                type="text"
                name="title"
                placeholder="Cut 2200 kcal"
                required
                maxLength={60}
                defaultValue={query.title}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-row-item">
              <label htmlFor="import-description">Description</label>
              <input
                id="import-description"
                type="text"
                name="description"
                placeholder="Optional — leave blank to use the one in the file"
                maxLength={100}
                defaultValue={query.description}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-row-item">
              <label htmlFor="import-file">Meal plan JSON</label>
              <input
                id="import-file"
                type="file"
                name="file"
                accept=".json,application/json"
                required
              />
            </div>
          </div>
          {/* apply travels in hidden inputs: rill's form serializer doesn't reliably send the clicked button */}
          <input type="hidden" name="apply" value="false" />
          <div className="admin-import-actions">
            <button type="submit" data-button-type="primary">
              Preview
            </button>
            <ResetButton path="/admin/import-meal-plan" />
          </div>
        </form>

        {importResult && <ImportResult result={importResult} />}
      </div>
    </main>
  );
};

const ImportResult = ({ result }) => {
  const { summary, errors = [], templateId, plan } = result;
  const [choices, setChoices] = useState(() => appliedChoices(summary));
  // a new preview brings the picks it applied
  useEffect(() => setChoices(appliedChoices(summary)), [summary]);

  const applied = appliedChoices(summary);
  const dirty = !sameChoices(choices, applied);
  const changed = changedNames(choices, applied);
  const choose = (name, value) =>
    setChoices((current) => {
      const { [name]: _removed, ...rest } = current;
      if (!value) return rest;
      const [source, ...id] = value.split("|");
      return { ...rest, [name]: { source, id: id.join("|") } };
    });
  const hiddenFields = summary && (
    <>
      <input type="hidden" name="coachEmail" value={summary.coach.email} />
      <input type="hidden" name="title" value={summary.title} />
      <input
        type="hidden"
        name="description"
        value={summary.description ?? ""}
      />
      <input type="hidden" name="plan" value={plan ?? ""} />
      <input type="hidden" name="choices" value={JSON.stringify(choices)} />
    </>
  );

  return (
    <section className="admin-import-result">
      {templateId && (
        <div className="admin-import-status" data-status="success">
          <strong>Imported</strong>
          <span>
            "{summary.title}" is now a meal plan template in{" "}
            {summary.coach.email}'s account (template {templateId}).
          </span>
        </div>
      )}

      {plan && !errors.length && (
        <form
          className="admin-import-status"
          data-status="ready"
          method="POST"
          action="/actions/admin-panel/import-meal-plan"
          encType="multipart/form-data"
        >
          <div>
            <strong>Meal plan preview OK</strong>
            <span>
              Nothing has been written yet. Importing creates the template
              {summary.newFoods.length > 0 &&
                " and the new custom foods listed below"}
              .{dirty && " Update the preview to apply your choices first."}
            </span>
          </div>
          {hiddenFields}
          <input type="hidden" name="apply" value="true" />
          <button type="submit" data-button-type="primary" disabled={dirty}>
            Import meal plan into {summary.coach.email}
          </button>
        </form>
      )}

      {errors.length > 0 && (
        <div className="admin-import-status" data-status="error">
          <strong>
            {errors.length} error{errors.length === 1 ? "" : "s"}
            {result.apply ? " — nothing written" : ""}
          </strong>
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {summary && (
        <ImportSummary
          summary={summary}
          choices={choices}
          changed={changed}
          onChoose={choose}
          canChoose={Boolean(plan)}
        />
      )}

      {plan && summary && dirty && (
        <form
          className="admin-import-pending"
          method="POST"
          action="/actions/admin-panel/import-meal-plan"
          encType="multipart/form-data"
        >
          <span>
            {changed.size} change{changed.size === 1 ? "" : "s"} not applied yet
          </span>
          {hiddenFields}
          <input type="hidden" name="apply" value="false" />
          <button type="submit" data-button-type="primary">
            Update preview
          </button>
        </form>
      )}
    </section>
  );
};

const ImportSummary = ({ summary, choices, changed, onChoose, canChoose }) => {
  // Defaults: the GUI and API deploy separately, so an older API may not send
  // every field yet.
  const {
    newFoods = [],
    newMeals = [],
    sources = {},
    items = [],
    matches = [],
    chosen = [],
  } = summary;
  const sourceEntries = Object.entries(sources);

  return (
    <>
      <h3>
        <span className="admin-import-kind" data-kind="meal-plan">
          Meal plan
        </span>
        {summary.title} <span>· {summary.coach.email}</span>
      </h3>
      {summary.description && (
        <p className="admin-import-description">{summary.description}</p>
      )}

      <dl className="admin-import-stats">
        <div>
          <dt>Mode</dt>
          <dd>{MODE_LABELS[summary.planMode] ?? summary.planMode}</dd>
        </div>
        {summary.weeks !== undefined && (
          <>
            <div>
              <dt>Weeks</dt>
              <dd>{summary.weeks}</dd>
            </div>
            <div>
              <dt>Days with foods</dt>
              <dd>{summary.days}</dd>
            </div>
          </>
        )}
        <div>
          <dt>Foods</dt>
          <dd>{summary.foods}</dd>
        </div>
      </dl>

      {sourceEntries.length > 0 && (
        <>
          <h3>Where the foods come from</h3>
          {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
          <section role="table-wrapper" data-borders-inside /* NOSONAR */>
            {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
            <table role="report-table" /* NOSONAR */>
              {/* biome-ignore lint/a11y/useValidAriaRole: report-header used for structure */}
              <thead role="report-header" /* NOSONAR */>
                <tr>
                  <th>Source</th>
                  <th>Items</th>
                </tr>
              </thead>
              <tbody>
                {sourceEntries.map(([source, count]) => (
                  <tr key={source}>
                    <td>{sourceLabel(source)}</td>
                    <td>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      {items.length > 0 && <FoodMatches items={items} />}

      {newMeals.length > 0 && <NewMeals meals={newMeals} />}

      {matches.length > 0 && <MatchesToReview matches={matches} />}

      {chosen.length > 0 && (
        <ChosenFoods
          foods={chosen}
          choices={choices}
          changed={changed}
          onChoose={onChoose}
          canChoose={canChoose}
        />
      )}

      {newFoods.length > 0 && (
        <NewFoods
          foods={newFoods}
          choices={choices}
          changed={changed}
          onChoose={onChoose}
          canChoose={canChoose}
        />
      )}
    </>
  );
};

/** Every food of the plan and where the import found it, to check the matches. */
const FoodMatches = ({ items }) => (
  <details className="admin-import-matches">
    <summary>Show every food and where it was found ({items.length})</summary>
    {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
    <section role="table-wrapper" data-borders-inside /* NOSONAR */>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" /* NOSONAR */>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-header used for structure */}
        <thead role="report-header" /* NOSONAR */>
          <tr>
            <th>Food in the file</th>
            <th>Amount</th>
            <th>Found in</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={`${i}-${item.name}`}>
              <td>{item.name}</td>
              <td>{amountText(item)}</td>
              <td>{sourceLabel(item.source)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  </details>
);

const reusedText = (meal) =>
  meal.differs
    ? "Already exists — reused, but its foods differ from the file"
    : "Already exists — reused";

const matchResult = (match) => {
  if (match.warning) return match.warning;
  if (match.branded) return "Branded — created from the estimate";
  return match.refused ? "Refused — created from the estimate" : "Used";
};

/**
 * Meals the file defines: created in the coach's library, unless the coach
 * already has one with that name (then it is reused as it is).
 */
const NewMeals = ({ meals }) => (
  <>
    <h3>Meals ({meals.length})</h3>
    <p>
      Each is added to the coach's saved meals and used by the plan. A name the
      coach already has is reused, never changed: check the ones marked as
      different, they may be another dish with the same name.
    </p>
    {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
    <section role="table-wrapper" data-borders-inside /* NOSONAR */>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" /* NOSONAR */>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-header used for structure */}
        <thead role="report-header" /* NOSONAR */>
          <tr>
            <th>Meal</th>
            <th>Description</th>
            <th>Foods</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {meals.map((meal) => (
            <tr key={meal.name}>
              <td>{meal.name}</td>
              <td>{meal.description ?? ""}</td>
              <td>{meal.foods}</td>
              <td>{meal.reused ? reusedText(meal) : "Will be created"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  </>
);

const kcalText = (kcal) => (kcal === undefined ? "—" : `${kcal} kcal`);

/**
 * MSB foods the file's names were matched to that deserve a look: branded
 * products, or calories far from the estimate in the file. A refused match was
 * not used: the food is created from the estimate instead.
 */
const MatchesToReview = ({ matches }) => (
  <>
    <h3>MSB matches to review ({matches.length})</h3>
    <p>
      Branded foods, foods whose calories differ from the estimate in the file,
      and library foods with impossible calories. Calories are per 100 g (or
      ml). A refused match is not used: the food is created from the estimate.
    </p>
    {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
    <section role="table-wrapper" data-borders-inside /* NOSONAR */>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" /* NOSONAR */>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-header used for structure */}
        <thead role="report-header" /* NOSONAR */>
          <tr>
            <th>Food in the file</th>
            <th>MSB food</th>
            <th>MSB</th>
            <th>Estimate</th>
            <th>Difference</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((match) => (
            <tr key={match.name}>
              <td>{match.name}</td>
              <td>
                {match.matched}
                {match.brand && ` (${match.brand})`}
              </td>
              <td>{kcalText(match.kcal)}</td>
              <td>{kcalText(match.estimateKcal)}</td>
              <td>{match.gapPct === undefined ? "—" : `${match.gapPct}%`}</td>
              <td>{matchResult(match)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  </>
);

const ChangedTag = () => (
  <span className="admin-import-changed">Changed</span>
);

/** The select to use an MSB food instead of creating a custom one. */
const FoodChoice = ({ food, options, choices, onChoose }) => {
  const current = choices[food];
  return (
    <select
      aria-label={`MSB food to use for ${food}`}
      value={current ? optionValue(current) : ""}
      onChange={(event) => onChoose(food, event.target.value)}
    >
      <option value="">Create a custom food</option>
      {options.map((option) => (
        <option key={optionValue(option)} value={optionValue(option)}>
          {optionText(option)}
        </option>
      ))}
    </select>
  );
};

/**
 * Foods found neither in the coach's library nor in MSB's databases: the
 * import creates them as custom foods, with macros Claude estimated — unless
 * the admin picks one of the closest MSB foods.
 */
const NewFoods = ({ foods, choices, changed, onChoose, canChoose }) => (
  <>
    <h3>New custom foods ({foods.length})</h3>
    <p>
      Not in the coach's library or MSB's usable databases. They will be added
      to the coach's custom foods with macros estimated from the file — review
      them. The closest foods are offered
      {canChoose ? " to use instead" : " as a hint"}; nothing is linked unless
      you pick one.
    </p>
    {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
    <section role="table-wrapper" data-borders-inside /* NOSONAR */>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" /* NOSONAR */>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-header used for structure */}
        <thead role="report-header" /* NOSONAR */>
          <tr>
            <th>Food</th>
            <th>Estimated macros</th>
            <th>{canChoose ? "Use instead" : "Closest MSB foods"}</th>
          </tr>
        </thead>
        <tbody>
          {foods.map((food) => (
            <tr key={food.name}>
              <td>
                {food.name}
                {changed.has(food.name) && <ChangedTag />}
              </td>
              <td>{macrosText(food)}</td>
              <td>
                {!food.suggestions?.length && "None found"}
                {food.suggestions?.length > 0 && canChoose && (
                  <FoodChoice
                    food={food.name}
                    options={food.suggestions}
                    choices={choices}
                    onChoose={onChoose}
                  />
                )}
                {food.suggestions?.length > 0 && !canChoose && (
                  <ul>
                    {food.suggestions.map((hint) => (
                      <li key={optionValue(hint)}>{optionText(hint)}</li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  </>
);

/** Foods the admin matched to an MSB food in an earlier preview. */
const ChosenFoods = ({ foods, choices, changed, onChoose, canChoose }) => (
  <>
    <h3>Foods matched by you ({foods.length})</h3>
    <p>
      These use the MSB food you picked instead of a custom food. Calories are
      per 100 g (or ml).
    </p>
    {/* biome-ignore lint/a11y/useValidAriaRole: table-wrapper used for structure */}
    <section role="table-wrapper" data-borders-inside /* NOSONAR */>
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" /* NOSONAR */>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-header used for structure */}
        <thead role="report-header" /* NOSONAR */>
          <tr>
            <th>Food in the file</th>
            <th>MSB food</th>
            <th>MSB</th>
            <th>Estimate</th>
            <th>Difference</th>
            <th>{canChoose ? "Change" : ""}</th>
          </tr>
        </thead>
        <tbody>
          {foods.map((food) => (
            <tr key={food.name}>
              <td>
                {food.name}
                {changed.has(food.name) && <ChangedTag />}
              </td>
              <td>
                {food.matched}
                {food.brand && ` (${food.brand})`}
              </td>
              <td>{kcalText(food.kcal)}</td>
              <td>{kcalText(food.estimateKcal)}</td>
              <td>{food.gapPct === undefined ? "—" : `${food.gapPct}%`}</td>
              <td>
                {canChoose && (
                  <FoodChoice
                    food={food.name}
                    options={food.options ?? []}
                    choices={choices}
                    onChoose={onChoose}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  </>
);

export default ImportMealPlan;
